import type { Region, Vec2 } from './geometry';

export type SmoothingLevel = 'low' | 'medium' | 'high';

export interface ProcessShapeOptions {
  minSpacing?: number;
  simplifyTolerance?: number;
  smoothing?: SmoothingLevel;
}

export interface ProcessShapeResult {
  region: Region | null;
  warning?: string;
  points: Vec2[];
}

const DEFAULT_MIN_SPACING = 1.1;
const DEFAULT_MIN_AREA = 6;

export function processFreehandShape(
  samples: readonly Vec2[],
  options: ProcessShapeOptions = {},
): ProcessShapeResult {
  const spaced = filterPointSpacing(samples, options.minSpacing ?? DEFAULT_MIN_SPACING);
  const simplified = simplifyRdp(spaced, options.simplifyTolerance ?? 1.25);
  const smoothed = smoothClosedPolygon(simplified, options.smoothing ?? 'low');
  return closeAndValidate(smoothed);
}

export function processPolygonShape(points: readonly Vec2[]): ProcessShapeResult {
  return closeAndValidate(points.map((p) => ({ ...p })));
}

export function circleToPolygon(center: Vec2, radius: number, segments = 24): Region {
  return {
    kind: 'polygon',
    points: Array.from({ length: Math.max(8, segments) }, (_, i) => {
      const angle = (i / Math.max(8, segments)) * Math.PI * 2;
      return { x: center.x + Math.cos(angle) * radius, z: center.z + Math.sin(angle) * radius };
    }),
  };
}

export function filterPointSpacing(points: readonly Vec2[], minSpacing: number): Vec2[] {
  const out: Vec2[] = [];
  for (const point of points) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(point.x - last.x, point.z - last.z) >= minSpacing)
      out.push({ ...point });
  }
  return out;
}

export function simplifyRdp(points: readonly Vec2[], tolerance: number): Vec2[] {
  if (points.length <= 3) return points.map((p) => ({ ...p }));
  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  simplifyRange(points, 0, points.length - 1, tolerance * tolerance, keep);
  return points.filter((_, i) => keep[i]).map((p) => ({ ...p }));
}

function simplifyRange(
  points: readonly Vec2[],
  first: number,
  last: number,
  toleranceSq: number,
  keep: boolean[],
): void {
  let maxDistance = 0;
  let index = first;
  for (let i = first + 1; i < last; i++) {
    const distance = distanceToSegmentSq(points[i], points[first], points[last]);
    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }
  if (maxDistance > toleranceSq) {
    keep[index] = true;
    simplifyRange(points, first, index, toleranceSq, keep);
    simplifyRange(points, index, last, toleranceSq, keep);
  }
}

export function smoothClosedPolygon(points: readonly Vec2[], level: SmoothingLevel): Vec2[] {
  const passes = level === 'low' ? 1 : level === 'high' ? 3 : 2;
  let out = points.map((p) => ({ ...p }));
  for (let pass = 0; pass < passes && out.length >= 3; pass++) {
    const next: Vec2[] = [];
    for (let i = 0; i < out.length; i++) {
      const a = out[i];
      const b = out[(i + 1) % out.length];
      next.push({ x: a.x * 0.75 + b.x * 0.25, z: a.z * 0.75 + b.z * 0.25 });
      next.push({ x: a.x * 0.25 + b.x * 0.75, z: a.z * 0.25 + b.z * 0.75 });
    }
    out = next;
  }
  return out;
}

export function closeAndValidate(points: readonly Vec2[]): ProcessShapeResult {
  const cleaned = dedupeClosingPoint(points).filter(
    (p) => Number.isFinite(p.x) && Number.isFinite(p.z),
  );
  if (cleaned.length < 3)
    return { region: null, points: cleaned, warning: 'Draw at least three distinct points.' };
  if (hasSelfIntersection(cleaned))
    return {
      region: null,
      points: cleaned,
      warning: 'Outline self-intersects. Undo and redraw a simpler boundary.',
    };
  const area = Math.abs(polygonArea(cleaned));
  if (area === 0) return { region: null, points: cleaned, warning: 'Outline has no usable area.' };
  if (area < DEFAULT_MIN_AREA)
    return {
      region: null,
      points: cleaned,
      warning: 'Shape is too small. Draw a larger boundary or zoom in before finishing.',
    };
  return { region: { kind: 'polygon', points: cleaned }, points: cleaned };
}

export function hasSelfIntersection(points: readonly Vec2[]): boolean {
  for (let i = 0; i < points.length; i++) {
    const a1 = points[i];
    const a2 = points[(i + 1) % points.length];
    for (let j = i + 1; j < points.length; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === points.length - 1)) continue;
      if (segmentsIntersect(a1, a2, points[j], points[(j + 1) % points.length])) return true;
    }
  }
  return false;
}

function dedupeClosingPoint(points: readonly Vec2[]): Vec2[] {
  const out = points.map((p) => ({ ...p }));
  const first = out[0];
  const last = out[out.length - 1];
  if (first && last && Math.hypot(first.x - last.x, first.z - last.z) < 0.001) out.pop();
  return out;
}

function polygonArea(points: readonly Vec2[]): number {
  return (
    points.reduce((sum, p, i) => {
      const q = points[(i + 1) % points.length];
      return sum + p.x * q.z - q.x * p.z;
    }, 0) / 2
  );
}

function distanceToSegmentSq(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq));
  const x = a.x + t * dx;
  const z = a.z + t * dz;
  return (p.x - x) ** 2 + (p.z - z) ** 2;
}

function segmentsIntersect(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const ccw = (p1: Vec2, p2: Vec2, p3: Vec2) =>
    (p3.z - p1.z) * (p2.x - p1.x) > (p2.z - p1.z) * (p3.x - p1.x);
  return ccw(a, c, d) !== ccw(b, c, d) && ccw(a, b, c) !== ccw(a, b, d);
}
